// Port of src/staleChunkReload.js.
//
// After a redeploy the old index.html in a live tab still references hashed chunk filenames that
// no longer exist. nginx answers those with `try_files ... /index.html`, i.e. HTTP 200 and an HTML
// body — so the dynamic import does NOT reject; the browser parses the HTML as a module and
// resolves it with no exports. The symptom is a screen that silently renders nothing.
//
// So we detect "module resolved but has no default export" and reload once, which fetches the new
// index.html and its current chunk names. The cooldown (persisted in sessionStorage so it survives
// the reload) stops a genuinely broken deploy from turning into a reload loop.

const RELOAD_COOLDOWN_MS = 10_000;
const STORAGE_KEY = "staleChunkReloadedAt";

// Set when we ask for a reload, and deliberately never cleared: `location.reload()` is
// asynchronous, so the page keeps running — and every other chunk that fails while it unloads is
// the same stale deploy, not new evidence that reloading did not work. Without this, a second
// failing chunk fell through the cooldown and surfaced a hard error on a page already on its way
// out. (The React tree hit exactly that in production: DASHBOARD-BH.)
let reloadInFlight = false;

// sessionStorage *throws* rather than returning null where storage is blocked (Safari private
// mode, third-party-cookie blocking in an iframe). Treat that as "nothing recorded" so a stale tab
// there still recovers once, instead of a recoverable deploy becoming a crash.
function lastReloadAt() {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY) || 0);
  } catch {
    return 0;
  }
}

function recordReload(at) {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(at));
  } catch {
    // Losing the stamp only costs us the loop guard on the next page life, which is better than
    // refusing to recover at all.
  }
}

// Returns true if a reload is under way — whether this call started it or an earlier one did.
export function reloadOnceForStaleChunk() {
  if (reloadInFlight) return true;
  if (Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS) return false;
  reloadInFlight = true;
  recordReload(Date.now());
  window.location.reload();
  return true;
}

// Wraps a route's `() => import("./screens/X.svelte")` loader. Returns the component, or — for the
// stale-chunk case — a promise that never settles, so the router keeps showing its loader while the
// page reloads. Inside the cooldown we surface a real error instead of hiding it.
export function loadScreen(loader) {
  return loader().then((module) => {
    if (module?.default) return module.default;
    if (reloadOnceForStaleChunk()) return new Promise(() => {});
    throw new Error("Screen chunk resolved with no component (stale deploy?)");
  });
}

// The other failure mode: the chunk request fails outright (offline, or the asset really is gone
// and nginx did not fall back). Vite fires `vite:preloadError` for that; same reload treatment.
export function installPreloadErrorHandler() {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}
